import { apiFetchWithRetry } from './apiFetch';

export type EngagementScope = 'affiliated' | 'all';

export interface EngagementStats {
  /** 'affiliated' | 'all' for an aggregate, 'school' for a single-school drill-down. */
  scope: 'affiliated' | 'all' | 'school';
  schoolId: string | null;
  schoolName: string | null;
  totalStudents: number;
  activeStudents: number;
  inactiveStudents: number;
  pendingInvites: number;
  acceptedInvites: number;
  totalAchievements: number;
  studentsWithAchievements: number;
  studentsWithHypepage: number;
  studentsWithResume: number;
  verifiedAchievements: number;
  hypesReceived: number;
  /** Welcome/tour card — what's left of "onboarding". */
  welcomeShown: number;
  tourCompleted: number;
  onboardingCompleted: number;
}

export interface EngagementSchoolRow {
  schoolId: string;
  schoolName: string;
  totalStudents: number;
  activeStudents: number;
  totalAchievements: number;
  studentsWithAchievements: number;
  verifiedAchievements: number;
  hypesReceived: number;
}

export const dashboardService = {
  /**
   * Engagement stats — admin-only on the server (403 for non-admins; the
   * retry wrapper signs the user out if they end up holding a non-admin
   * token here). No schoolId → aggregate respecting `scope`; a schoolId →
   * that single school's strip.
   */
  getEngagement: async (scope: EngagementScope, schoolId?: string): Promise<EngagementStats> => {
    const params = new URLSearchParams({ scope });
    if (schoolId) params.set('schoolId', schoolId);
    const response = await apiFetchWithRetry(`/v1/dashboard/admins/engagement?${params.toString()}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch engagement stats (${response.status})`);
    }
    return (await response.json()) as EngagementStats;
  },

  /** Per-school rows for the comparison table + the school picker. */
  getEngagementBySchool: async (): Promise<EngagementSchoolRow[]> => {
    const response = await apiFetchWithRetry('/v1/dashboard/admins/engagement/by-school');
    if (!response.ok) {
      throw new Error(`Failed to fetch per-school engagement (${response.status})`);
    }
    const data = (await response.json()) as { schools: EngagementSchoolRow[] };
    return data.schools;
  },
};
